const ID_MASK = 0x5bd1e995;
const CHECK_MOD = 1296;

export type PublicIdPrefix =
  | 'auc'
  | 'str'
  | 'arc'
  | 'post'
  | 'conv'
  | 'tick'
  | 'req'
  | 'offer'
  | 'trade'
  | 'usr'
  | 'creator'
  | 'app';

const PUBLIC_PREFIXES = new Set<PublicIdPrefix>([
  'auc',
  'str',
  'arc',
  'post',
  'conv',
  'tick',
  'req',
  'offer',
  'trade',
  'usr',
  'creator',
  'app',
]);

const normalizeId = (id: string | number | null | undefined) => {
  if (id === null || id === undefined) return null;
  const raw = String(id).trim();
  if (!raw) return null;
  if (!/^\d+$/.test(raw)) return null;
  const parsed = Number(raw);
  return Number.isSafeInteger(parsed) && parsed >= 0 ? parsed : null;
};

const checksum = (id: number) => ((id * 97 + 53) % CHECK_MOD).toString(36).padStart(2, '0');

export const encodePublicId = (
  id: string | number | null | undefined,
  prefix: PublicIdPrefix = 'usr'
) => {
  const normalized = normalizeId(id);
  if (normalized === null) return id === null || id === undefined ? '' : String(id);

  const masked = (normalized ^ ID_MASK) >>> 0;
  return `${prefix}-${masked.toString(36)}${checksum(normalized)}`;
};

export const decodePublicId = (value: string | number | null | undefined) => {
  if (value === null || value === undefined) return '';

  const raw = String(value).trim();
  if (!raw || /^\d+$/.test(raw)) return raw;

  const token = raw.split('/').pop()?.split('?')[0]?.split('#')[0] || raw;
  const separatorIndex = token.lastIndexOf('-');
  if (separatorIndex <= 0) return raw;

  const prefix = token.slice(0, separatorIndex) as PublicIdPrefix;
  if (!PUBLIC_PREFIXES.has(prefix)) return raw;

  const body = token.slice(separatorIndex + 1);
  if (body.length <= 2) return raw;

  const encoded = body.slice(0, -2);
  const expectedCheck = body.slice(-2);
  const masked = Number.parseInt(encoded, 36);
  if (!Number.isFinite(masked)) return raw;

  const decoded = (masked ^ ID_MASK) >>> 0;
  if (checksum(decoded) !== expectedCheck) return raw;

  return String(decoded);
};

export const decodePublicNumber = (value: string | number | null | undefined) => {
  const decoded = decodePublicId(value);
  const normalized = normalizeId(decoded);
  return normalized;
};

export const auctionPath = (id: string | number | null | undefined) => `/game-a-rama/${encodePublicId(id, 'auc')}`;
export const streamPath = (id: string | number | null | undefined) => `/stream/${encodePublicId(id, 'str')}`;
export const streamFullscreenPath = (id: string | number | null | undefined) => `/stream/fullscreen/${encodePublicId(id, 'str')}`;
export const creatorStreamPath = (id: string | number | null | undefined) => `/stream/creator/fullscreen/${encodePublicId(id, 'str')}`;
export const streamArchivePath = (id: string | number | null | undefined) => `/streaming/archive/${encodePublicId(id, 'arc')}`;
export const postPath = (id: string | number | null | undefined) => `/post/${encodePublicId(id, 'post')}`;
export const conversationPath = (id: string | number | null | undefined) => `/messages/${encodePublicId(id, 'conv')}`;
export const ticketPath = (id: string | number | null | undefined) => `/support/tickets/${encodePublicId(id, 'tick')}`;
export const privateSessionPath = (id: string | number | null | undefined) => `/private-session/${encodePublicId(id, 'req')}`;
export const profilePath = (usernameOrId: string | number | null | undefined) => `/profile/${encodePublicId(usernameOrId, 'usr')}`;
export const otcOfferPath = (id: string | number | null | undefined) => `/otc/offers/${encodePublicId(id, 'offer')}`;
export const otcTradePath = (id: string | number | null | undefined) => `/otc/trades/${encodePublicId(id, 'trade')}`;
export const creatorDetailPath = (id: string | number | null | undefined) => `/manager/creators/${encodePublicId(id, 'creator')}`;
export const creatorReapplyPath = (id: string | number | null | undefined) => `/manager/creators/new?reapply=${encodePublicId(id, 'app')}`;
export const userFollowersPath = (id: string | number | null | undefined) => `/users/${encodePublicId(id, 'usr')}/followers`;
export const userFollowingPath = (id: string | number | null | undefined) => `/users/${encodePublicId(id, 'usr')}/following`;
